/**
 * 同意画面。プライバシーポリシーへの明示同意を取る。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の同意画面コンポーネント
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import type { MessagesCatalog } from '../i18n/index.js';

interface ConsentPageProps {
  messages: MessagesCatalog;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 個人情報保護同意画面。同意後に LanguageSelectPage に遷移する
 * @graph-connects none
 */
export function ConsentPage({ messages, onAccept, onDecline }: ConsentPageProps): ReactNode {
  const { consent } = messages;
  return (
    <Card>
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2.5">
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            {consent.title}
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed">{consent.summary}</p>
        </div>
        <ul className="flex flex-col gap-3">
          {consent.items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] p-4 ring-1 ring-[var(--color-border)]"
            >
              <span
                aria-hidden
                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
                {item}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onDecline}>
            {consent.declineLabel}
          </Button>
          <Button variant="primary" onClick={onAccept}>
            {consent.agreeLabel}
          </Button>
        </div>
      </div>
    </Card>
  );
}
